import React, { useEffect, useState } from 'react';
import { useSupportContext } from './SupportContext';
import { useAppContext } from '../../contexts/Storage';
import { sendSupportMessage } from '../../services/supportService';
import { Button } from '../ui/Button';

const SupportPanel: React.FC = () => {
  const { isOpen, closeSupport } = useSupportContext();
  const { user, token } = useAppContext();

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | ''; text: string }>({
    type: '',
    text: '',
  });

  useEffect(() => {
    if (isOpen) {
      setEmail(user?.email || '');
      setMessage('');
      setWebsite('');
      setStatus({ type: '', text: '' });
    }
  }, [isOpen, user?.email]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setStatus({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }
    if (!trimmedMessage) {
      setStatus({ type: 'error', text: 'Please write a message.' });
      return;
    }

    setIsSending(true);
    setStatus({ type: '', text: '' });

    try {
      if (website) {
        setStatus({ type: 'success', text: 'Sent. We will reply by email.' });
        setMessage('');
        return;
      }

      const result = await sendSupportMessage({
        email: trimmedEmail,
        message: trimmedMessage,
        name: user?.username || user?.firstName || '',
        token: token || user?.accessToken || null,
      });

      if (result.success) {
        const sentTo = result.data?.message || `Sent. We will reply to ${trimmedEmail}.`;
        setStatus({ type: 'success', text: sentTo });
        setMessage('');
      } else if (result.code === 429) {
        setStatus({ type: 'error', text: 'Too many tries. Wait a minute and try again.' });
      } else {
        setStatus({ type: 'error', text: result.error || 'Could not send. Try again.' });
      }
    } catch {
      setStatus({ type: 'error', text: 'Could not reach the server. Check your connection and try again.' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:justify-start p-4 sm:pl-6 sm:pr-24"
      style={{ backgroundColor: 'rgba(30, 30, 30, 0.35)' }}
      onClick={closeSupport}
    >
      <div
        className="w-full max-w-[380px] rounded-2xl overflow-hidden flex flex-col shadow-lg"
        style={{
          backgroundColor: 'var(--light)',
          border: '2px solid var(--primary)',
          maxHeight: 'min(540px, 90vh)',
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="support-title"
      >
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ backgroundColor: 'var(--primary)', color: '#FFFFFF' }}
        >
          <div>
            <h2 id="support-title" className="font-semibold text-sm leading-tight">
              Talk to a person
            </h2>
            <p className="text-[11px] opacity-90 leading-tight">We will email you back</p>
          </div>
          <button
            type="button"
            onClick={closeSupport}
            className="p-1.5 rounded-lg hover:opacity-70"
            aria-label="Close help"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ backgroundColor: 'var(--bg-grey-light)' }}>
          <div
            className="rounded-2xl rounded-tl-sm px-3 py-2 text-sm"
            style={{
              backgroundColor: 'var(--light)',
              color: 'var(--dark)',
              border: '1px solid var(--grey-light)',
            }}
          >
            Ask us anything about your shop books. We will reply by email. No phone.
          </div>

          {status.type === 'success' && (
            <div
              className="rounded-2xl rounded-tr-sm px-3 py-2 text-sm ml-8"
              style={{ backgroundColor: 'var(--primary)', color: '#FFFFFF' }}
            >
              {status.text}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-4 py-3 space-y-2 border-t" style={{ borderColor: 'var(--grey-light)' }}>
          <label className="block text-xs" style={{ color: 'var(--grey-dark)' }}>
            Your email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg text-sm text-dark bg-light border border-grey_light focus:outline-none focus:border-secondary"
              placeholder="you@email.com"
              autoComplete="email"
              required
            />
          </label>
          <label className="block text-xs" style={{ color: 'var(--grey-dark)' }}>
            Message
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg text-sm text-dark bg-light border border-grey_light focus:outline-none focus:border-secondary resize-none"
              rows={3}
              maxLength={4000}
              placeholder="What do you need help with?"
              required
            />
          </label>
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
            style={{ position: 'absolute', left: '-9999px' }}
          />
          {status.type === 'error' && (
            <p className="text-sm" style={{ color: 'var(--danger)' }}>{status.text}</p>
          )}
          <Button
            type="submit"
            variant="filled-primary"
            className="w-full"
            disabled={isSending}
          >
            {isSending ? 'Sending...' : 'Send'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default SupportPanel;
